import React, { useRef, useMemo, useState, useEffect, useCallback } from 'react';

/**
 * TimelineScrubber - Enhanced timeline with debounced frame navigation
 *
 * Features:
 * - Debounced API calls (100ms) for smooth scrubbing
 * - requestAnimationFrame for visual updates
 * - Event marker dots for tracked points
 * - Hover preview tooltip with frame number
 */
const TimelineScrubber = ({
  currentFrame = 0,
  totalFrames = 0,
  onFrameChange,
  trackedFrames = [],
  currentTime = 0,
  duration = 0,
  fps = 30
}) => {
  const progressBarRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [hoveredFrame, setHoveredFrame] = useState(null);
  const [localFrame, setLocalFrame] = useState(currentFrame);
  const debounceTimerRef = useRef(null);

  // Sync local frame with prop
  useEffect(() => {
    setLocalFrame(currentFrame);
  }, [currentFrame]);

  /**
   * Debounced frame change handler (100ms)
   * Visual update is immediate, API call is debounced
   */
  const debouncedFrameChange = useCallback((frameIndex) => {
    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer
    debounceTimerRef.current = setTimeout(() => {
      if (onFrameChange) {
        onFrameChange(frameIndex);
      }
    }, 100); // 100ms debounce
  }, [onFrameChange]);

  /**
   * Handle scrub - immediate visual update, debounced API call
   */
  const handleScrub = useCallback((e) => {
    if (!progressBarRef.current || totalFrames === 0) return;

    requestAnimationFrame(() => {
      const rect = progressBarRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const percentage = Math.min(Math.max(x / rect.width, 0), 1);
      const frame = Math.floor(percentage * totalFrames);

      // Immediate visual update
      setLocalFrame(frame);

      // Debounced API call
      debouncedFrameChange(frame);
    });
  }, [totalFrames, debouncedFrameChange]);

  /**
   * Handle mouse down - start dragging
   */
  const handleMouseDown = (e) => {
    setIsDragging(true);
    handleScrub(e);
  };

  /**
   * Handle mouse move - continue scrubbing if dragging
   */
  const handleMouseMove = (e) => {
    if (isDragging) {
      handleScrub(e);
    }

    // Update hover preview
    if (progressBarRef.current && totalFrames > 0) {
      const rect = progressBarRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const percentage = Math.min(Math.max(x / rect.width, 0), 1);
      const frame = Math.floor(percentage * totalFrames);
      setHoveredFrame(frame);
    }
  };

  /**
   * Handle mouse up - stop dragging
   */
  const handleMouseUp = () => {
    setIsDragging(false);
  };

  /**
   * Handle mouse leave - clear hover
   */
  const handleMouseLeave = () => {
    setHoveredFrame(null);
    if (isDragging) {
      setIsDragging(false);
    }
  };

  // Add/remove global mouse listeners for drag
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove]);

  // Calculate progress percentage
  const progressPercent = useMemo(() => {
    if (totalFrames === 0) return 0;
    return (localFrame / totalFrames) * 100;
  }, [localFrame, totalFrames]);

  // Format time for display
  const formatTime = (frame) => {
    const seconds = frame / fps;
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="w-full">
      {/* Timeline container */}
      <div
        className="relative w-full h-12 flex items-center px-2 group cursor-pointer"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        {/* Track Background */}
        <div
          className="relative w-full h-2 bg-zinc-800 rounded-sm overflow-visible"
          ref={progressBarRef}
        >
          {/* Progress Fill */}
          <div
            className="absolute top-0 left-0 h-full bg-cyan-500 transition-all duration-100 ease-linear z-10"
            style={{ width: `${progressPercent}%` }}
          >
            {/* Playhead Handle */}
            <div className="absolute right-0 top-1/2 transform translate-x-1/2 -translate-y-1/2 w-4 h-4 bg-cyan-400 border-2 border-white rounded-full shadow-lg scale-100 group-hover:scale-125 transition-transform duration-200" />
          </div>

          {/* Tracked frame markers */}
          {trackedFrames.map((frameIdx, idx) => {
            if (totalFrames === 0) return null;
            const leftPercent = (frameIdx / totalFrames) * 100;

            return (
              <div
                key={`${frameIdx}-${idx}`}
                className="absolute top-1/2 transform -translate-y-1/2 w-2 h-2 rounded-full bg-green-400 border border-green-300 z-20 shadow-sm"
                style={{ left: `${leftPercent}%` }}
                title={`Tracked at frame ${frameIdx}`}
              />
            );
          })}

          {/* Hover indicator line */}
          {hoveredFrame !== null && !isDragging && (
            <div
              className="absolute top-0 h-full w-px bg-cyan-400/50 z-5 pointer-events-none"
              style={{ left: `${(hoveredFrame / totalFrames) * 100}%` }}
            />
          )}
        </div>

        {/* Hover tooltip */}
        {hoveredFrame !== null && !isDragging && (
          <div
            className="absolute bottom-full mb-2 pointer-events-none"
            style={{
              left: `${(hoveredFrame / totalFrames) * 100}%`,
              transform: 'translateX(-50%)',
            }}
          >
            <div className="bg-black/90 border border-cyan-500/30 rounded px-2 py-1 shadow-xl">
              <p className="text-xs text-cyan-400 font-mono whitespace-nowrap">
                Frame {hoveredFrame}
              </p>
              <p className="text-xs text-zinc-400 font-mono">
                {formatTime(hoveredFrame)}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Frame info display */}
      <div className="flex justify-between items-center px-2 mt-1">
        <span className="text-xs text-zinc-400 font-mono">
          Frame {localFrame} / {totalFrames}
        </span>
        <span className="text-xs text-zinc-400 font-mono">
          {formatTime(localFrame)} / {formatTime(totalFrames)}
        </span>
      </div>
    </div>
  );
};

export default TimelineScrubber;
